<template>
  <div class="h-100 d-flex flex-column">
    <div class="d-flex mb-3 mt-1">
      <PayersGroupFilterDropdown
        :selected.sync="groupFilter"
        class="mr-3"
        hide-edit
      />
      <Tippy
        v-if="showOrderSubscriptionFilter"
        ref="orderFilterPopover"
        :arrow="true"
        trigger="click"
        interactive
        theme="light"
        placement="bottom"
      >
        <template #trigger>
          <div class="sort-option-selected mr-3">
            <div
              class="font-weight-bold text-secondary"
              style="font-size: 10px; min-width: 180px"
            >
              {{ orderSubscriptionFilterTitle }}
            </div>
            <div style="font-size: 14px">
              {{ orderSubscriptionFilters.length }} /
              {{ orderSubscriptionFilterOptions.length }}
            </div>
          </div>
        </template>

        <div class="dropdown-scroll">
          <div
            v-for="(option, index) in orderSubscriptionFilterOptions"
            :key="option.key"
            class="sort-option text-left d-flex align-items-center"
            :class="{
              first: index === 0,
              'font-weight-bold': orderSubscriptionFilters.includes(option.key),
            }"
            @click="toggleOrderSubscriptionFilter(option.key)"
          >
            <Checkbox
              :value="orderSubscriptionFilters.includes(option.key)"
              :emit="false"
              style="margin-left: -0.5rem"
            />
            <div class="flex-grow-1">
              {{ option.text }}
            </div>
          </div>
        </div>
      </Tippy>
      <SearchBox
        v-model="search"
        lazy
        class="mr-3"
      />
    </div>
    <div class="d-flex flex-column h-100">
      <div>
        <div class="row no-gutters align-items-center">
          <div class="col-7 d-flex align-items-center">
            <Checkbox
              :value="allToggled"
              class="pr-1 align-items-center"
              @update:value="toggleAll($event)"
            >
              <div class="mb-0 label sm-label">
                {{ $t('general.selectAll') }}
              </div>
            </Checkbox>
          </div>
        </div>

        <hr class="mb-1 mt-2">
      </div>
      <div
        ref="payersList"
        style="overflow: auto; padding-bottom: 20px"
        :style="{ height: `calc(100vh - ${payersListHeight}px)` }"
      >
        <div
          v-for="payer in filteredPayers"
          :key="payer.id"
          data-test="client-list-element"
          class="client-row w-100"
          @click="togglePayer(payer)"
        >
          <SimplePayerRow
            class="list-complete-item"
            v-bind="payer"
            :checked="payer.checked"
            :disabled="true"
            inline-code
          >
            <template #content>
              <template v-if="payer.checked">
                <Loader v-if="offerPending" />
                <div
                  v-else-if="payer.listings"
                  class="d-flex align-items-center mr-4"
                >
                  <BFormInput
                    v-if="payer.canOrderMany"
                    v-model="orderCount[payer.id]"
                    type="number"
                    size="sm"
                    class="mr-2"
                    placeholder="1"
                    style="min-width: 60px; width: 60px"
                    @click.stop
                    @change="emitOrderCount"
                  />

                  <ListingInfoPopup
                    v-for="l in payer.listings"
                    :key="l.id"
                    :listing="l"
                  >
                    <div class="badge badge-primary px-2 mr-1">
                      {{ l.name }}
                    </div>
                  </ListingInfoPopup>

                  <div
                    v-if="payer.listings.length === 0"
                    class="badge badge-danger px-2"
                  >
                    {{ $t('shop.offer.noMatchingOffer') }}
                  </div>
                </div>
              </template>
              <div
                v-if="existingOrdersSubscriptions[payer.id] && existingOrdersSubscriptions[payer.id].length > 0"
                class="d-flex align-items-center mr-4"
              >
                <span
                  v-for="item in existingOrdersSubscriptions[payer.id]"
                  :key="item.id"
                  class="badge badge-secondary px-2 mr-1"
                >
                  {{ item.name }}
                </span>
              </div>
            </template>
          </SimplePayerRow>
        </div>
        <InfinityScrollCursor
          ref="cursor"
          @request="request"
        />
        <div v-if="busy">
          <Loader />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import InfinityScrollCursor from '@/components/InfinityScrollCursor';
import PayersGroupFilterDropdown from '@/components/payers/PayersGroupFilterDropdown';
import SimplePayerRow from '@/components/payers/SimplePayerRow';
import ListingInfoPopup from '@/components/shop/ListingInfoPopup';
import Checkbox from '@/components/utils/Checkbox';
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

export default {
  props: {
    selected: {
      type: Array,
      default: () => [],
    },
    catalogId: String,
    productIds: Array,
    selectedListings: Array,
    selectedDays: {
      type: Array,
      default: () => [],
    },
    subscriptionsOnly: Boolean,
  },
  data: () => ({
    search: '',
    filter: [],
    groupFilter: '#all',
    orderSubscriptionFilters: ['with', 'without'],
    orderSubscriptionMatches: {},
    existingOrdersSubscriptions: {},
    orderFilterRequestId: 0,
    bottom: false,
    busy: false,
    bulkFetching: false,
    payers: [],
    orderCount: {},
    offer: [],
    take: 50,
    skip: 0,
    requestId: 0,
    payersListHeight: 0,
    offerPending: false,
  }),
  components: {
    PayersGroupFilterDropdown,
    InfinityScrollCursor,
    ListingInfoPopup,
    SimplePayerRow,
    Checkbox,
  },
  inject: ['pageExpand'],
  computed: {
    ...mapGetters('shop', ['listings', 'catalogDetails']),
    showOrderSubscriptionFilter() {
      const productCount = this.productIds?.length || 0;
      const listingCount = this.selectedListings?.length || 0;
      const hasProducts = productCount + listingCount > 0;

      // For subscriptions, show filter if products selected
      if (this.subscriptionsOnly) {
        return hasProducts;
      }

      // For orders, show filter if products AND time period selected
      const hasDays = this.selectedDays?.length > 0;
      return hasProducts && hasDays;
    },
    orderSubscriptionFilterTitle() {
      return this.subscriptionsOnly ? this.$t('shop.subscriptions') : this.$t('shop.orders');
    },
    orderSubscriptionFilterOptions() {
      if (this.subscriptionsOnly) {
        return [
          {
            key: 'with',
            text: this.$t('shop.orderFilter.withSelectedSubscriptions'),
          },
          {
            key: 'without',
            text: this.$t('shop.orderFilter.withoutSelectedSubscriptions'),
          },
        ];
      }

      return [
        {
          key: 'with',
          text: this.$t('shop.orderFilter.withSelectedOrders'),
        },
        {
          key: 'without',
          text: this.$t('shop.orderFilter.withoutSelectedOrders'),
        },
      ];
    },
    matchListingIds() {
      const selected = new Set(this.selectedListings || []);
      const productIds = this.productIds || [];

      if (productIds.length > 0 && Array.isArray(this.listings)) {
        this.listings
          .filter((listing) => (listing.productIds || []).some((id) => productIds.includes(id)))
          .forEach((listing) => selected.add(listing.id));
      }

      return Array.from(selected);
    },
    payersWithOffer() {
      return this.payers.map((p) => ({
        ...p,
        checked: this.selected.some((el) => el.id === p.id),
        listings: (this.offer.find((el) => el.payerId === p.id)?.listingIds || []).map((id) =>
          this.listings.find((l) => l.id === id)),
        canOrderMany: this.offer.find((el) => el.payerId === p.id)?.canOrderMany || false,
      }));
    },
    filteredPayers() {
      if (!this.showOrderSubscriptionFilter) {
        return this.payersWithOffer;
      }

      const withSelected = this.orderSubscriptionFilters.includes('with');
      const withoutSelected = this.orderSubscriptionFilters.includes('without');

      if (withSelected && withoutSelected) {
        return this.payersWithOffer;
      }

      return this.payersWithOffer.filter((payer) => {
        const hasMatch = this.orderSubscriptionMatches[payer.id] || false;
        return hasMatch ? withSelected : withoutSelected;
      });
    },
    allToggled: {
      get() {
        return this.payers.length > 0 && this.payers.every((x) => this.selected.some((y) => x.id === y.id));
      },
      set() {},
    },
  },
  watch: {
    search() {
      this.skip = 0;
      this.bottom = false;
      this.busy = false;
      this.payers = [];
      this.requestId += 1;
      this.request();
    },
    groupFilter() {
      this.skip = 0;
      this.bottom = false;
      this.busy = false;
      this.payers = [];
      this.requestId += 1;
      if (this.showOrderSubscriptionFilter && this.orderSubscriptionFilters.length < 2) {
        this.fetchBulkPayers();
      } else {
        this.request();
      }
    },
    productIds() {
      this.reloadOffer();
      this.refreshOrderSubscriptionMatches();
      if (this.showOrderSubscriptionFilter && this.orderSubscriptionFilters.length < 2) {
        this.skip = 0;
        this.bottom = false;
        this.busy = false;
        this.payers = [];
        this.requestId += 1;
        this.fetchBulkPayers();
      }
    },
    selectedListings() {
      this.reloadOffer();
      this.refreshOrderSubscriptionMatches();
      if (this.showOrderSubscriptionFilter && this.orderSubscriptionFilters.length < 2) {
        this.skip = 0;
        this.bottom = false;
        this.busy = false;
        this.payers = [];
        this.requestId += 1;
        this.fetchBulkPayers();
      }
    },
    selectedDays() {
      this.refreshOrderSubscriptionMatches();
      this.fetchExistingOrdersSubscriptions();
    },
    listings() {
      if (this.showOrderSubscriptionFilter) {
        this.refreshOrderSubscriptionMatches();
      }
    },
    orderSubscriptionFilters() {
      if (!this.showOrderSubscriptionFilter) {
        return;
      }
      this.skip = 0;
      this.bottom = false;
      this.busy = false;
      this.payers = [];
      this.requestId += 1;
      this.fetchBulkPayers();
    },
  },
  methods: {
    ...mapActions(['getPayers']),
    ...mapActions('shop', ['findPayersOffer', 'getOrdersList', 'getSubscriptionsList']),
    toggleOrderSubscriptionFilter(key) {
      if (this.orderSubscriptionFilters.includes(key)) {
        this.orderSubscriptionFilters = this.orderSubscriptionFilters.filter((x) => x !== key);
        return;
      }

      this.orderSubscriptionFilters = [...this.orderSubscriptionFilters, key];
    },
    resolveOrdersDateRange() {
      if (!this.selectedDays || this.selectedDays.length === 0) {
        return {};
      }

      const sorted = [...this.selectedDays].sort((a, b) => moment(a).diff(moment(b)));
      const from = moment(sorted[0]).format('YYYY-MM-DD');
      const last = sorted[sorted.length - 1];
      const increment = this.catalogDetails?.orderSchema === 'Monthly' ? 'month' : 'day';
      const to = moment(last).add(1, increment).format('YYYY-MM-DD');

      return { from, to };
    },
    fetchBulkPayers() {
      if (this.bulkFetching) {
        return Promise.resolve();
      }

      const id = this.requestId;
      this.bulkFetching = true;

      return this.getPayers({
        params: {
          query: {
            skip: this.skip,
            take: 300,
            group: this.groupFilter === '#all' ? undefined : this.groupFilter,
            search: this.search.length === 0 ? undefined : this.search,
          },
        },
      })
        .then(({ data }) => {
          if (id !== this.requestId) {
            this.bulkFetching = false;
            return Promise.resolve();
          }

          // Add new payers to existing list
          const uniquePayers = data.filter((x) => !this.payers.some((y) => y.id === x.id));
          this.payers = [...this.payers, ...uniquePayers];

          // Mark bottom if less than requested
          if (data.length < 300) {
            this.bottom = true;
          }

          this.skip += 300;

          return this.refreshOrderSubscriptionMatches().then(() => {
            this.fetchExistingOrdersSubscriptions();
            // Wait for DOM to update with filtered results
            this.$nextTick(() => {
              const container = this.$refs.payersList;
              if (!container) {
                this.bulkFetching = false;
                return;
              }

              const { scrollHeight, clientHeight } = container;

              // If filtered results don't fill screen and more data exists, fetch more
              if (scrollHeight <= clientHeight && !this.bottom && id === this.requestId) {
                this.bulkFetching = false;
                this.fetchBulkPayers();
              } else {
                this.bulkFetching = false;
              }
            });
          });
        })
        .catch(() => {
          this.bulkFetching = false;
        });
    },
    fetchExistingOrdersSubscriptions() {
      if (this.payers.length === 0) {
        this.existingOrdersSubscriptions = {};
        return Promise.resolve();
      }

      const payerIds = this.payers.map((p) => p.id);

      if (this.subscriptionsOnly) {
        return this.getSubscriptionsList({
          params: {
            query: {
              catalogId: this.catalogId,
              groupKey: 'none',
              reserveQueue: false,
            },
          },
          data: {
            payerIds,
            groupBy: 'none',
          },
        })
          .then(({ data }) => {
            const grouped = {};
            data.forEach((item) => {
              if (!grouped[item.payerId]) {
                grouped[item.payerId] = [];
              }
              const listing = this.listings.find((l) => l.id === item.listingId);
              if (listing) {
                grouped[item.payerId].push({
                  id: `${item.payerId}-${item.listingId}`,
                  name: listing.name,
                });
              }
            });
            this.existingOrdersSubscriptions = grouped;
          })
          .catch(() => {
            this.existingOrdersSubscriptions = {};
          });
      }

      const { from, to } = this.resolveOrdersDateRange();

      // Skip fetching orders if no date range selected
      if (!from || !to) {
        this.existingOrdersSubscriptions = {};
        return Promise.resolve();
      }

      return this.getOrdersList({
        params: {
          query: {
            catalogId: this.catalogId,
            groupKey: 'none',
            reserveQueue: false,
            from,
            to,
          },
        },
        data: {
          payerIds,
          groupBy: 'none',
          showUnpaid: true,
          days: this.selectedDays,
        },
      })
        .then(({ data }) => {
          const grouped = {};
          data.forEach((item) => {
            if (!grouped[item.payerId]) {
              grouped[item.payerId] = [];
            }
            const listing = this.listings.find((l) => l.id === item.productSetId);
            if (listing) {
              let dayFormatted = '';
              if (item.day) {
                const isMonthly = this.catalogDetails?.orderSchema === 'Monthly';
                dayFormatted = moment(item.day).format(isMonthly ? 'MMM' : 'DD.MM');
              }
              grouped[item.payerId].push({
                id: `${item.payerId}-${item.productSetId}-${item.day}`,
                name: dayFormatted ? `${listing.name} (${dayFormatted})` : listing.name,
              });
            }
          });
          this.existingOrdersSubscriptions = grouped;
        })
        .catch(() => {
          this.existingOrdersSubscriptions = {};
        });
    },
    refreshOrderSubscriptionMatches() {
      if (!this.showOrderSubscriptionFilter) {
        this.orderSubscriptionMatches = {};
        this.orderSubscriptionFilters = ['with', 'without'];
        return Promise.resolve();
      }

      if (this.payers.length === 0) {
        this.orderSubscriptionMatches = {};
        return Promise.resolve();
      }

      const listingIds = this.matchListingIds;
      if (listingIds.length === 0) {
        this.orderSubscriptionMatches = {};
        return Promise.resolve();
      }

      const requestId = this.orderFilterRequestId + 1;
      this.orderFilterRequestId = requestId;
      const payerIds = this.payers.map((payer) => payer.id);

      const matches = payerIds.reduce((acc, id) => {
        acc[id] = false;
        return acc;
      }, {});

      if (this.subscriptionsOnly) {
        return this.getSubscriptionsList({
          params: {
            query: {
              catalogId: this.catalogId,
              groupKey: 'none',
              reserveQueue: false,
            },
          },
          data: {
            payerIds,
            groupBy: 'none',
          },
        })
          .then(({ data }) => {
            if (requestId !== this.orderFilterRequestId) {
              return;
            }

            data.forEach((item) => {
              if (listingIds.includes(item.listingId)) {
                matches[item.payerId] = true;
              }
            });

            this.orderSubscriptionMatches = matches;
          })
          .catch(() => {
            if (requestId !== this.orderFilterRequestId) {
              return;
            }
            this.orderSubscriptionMatches = {};
          });
      }

      const { from, to } = this.resolveOrdersDateRange();

      return this.getOrdersList({
        params: {
          query: {
            catalogId: this.catalogId,
            groupKey: 'none',
            reserveQueue: false,
            from,
            to,
          },
        },
        data: {
          payerIds,
          groupBy: 'none',
          showUnpaid: true,
          days: this.selectedDays,
        },
      })
        .then(({ data }) => {
          if (requestId !== this.orderFilterRequestId) {
            return;
          }

          data.forEach((item) => {
            if (listingIds.includes(item.productSetId)) {
              matches[item.payerId] = true;
            }
          });

          this.orderSubscriptionMatches = matches;
        })
        .catch(() => {
          if (requestId !== this.orderFilterRequestId) {
            return;
          }
          this.orderSubscriptionMatches = {};
        });
    },
    emitOrderCount() {
      this.$emit('orderCount', this.orderCount);
    },
    request(all) {
      if ((this.bottom || this.busy) && !all) return Promise.resolve();
      this.busy = true;
      this.error = null;
      const id = this.requestId;

      return this.getPayers({
        params: {
          query: {
            skip: all ? 0 : this.skip,
            take: all ? -1 : this.take,
            group: this.groupFilter === '#all' ? undefined : this.groupFilter,
            search: this.search.length === 0 ? undefined : this.search,
          },
        },
      })
        .then(({ data }) => {
          if (all) {
            this.payers = data;
            this.bottom = true;
            this.busy = false;
            return this.refreshOrderSubscriptionMatches().then(() => {
              this.fetchExistingOrdersSubscriptions();
            });
          }

          if (id !== this.requestId) {
            return Promise.resolve();
          }

          this.skip += this.take;

          const uniquePayers = data.filter((x) => !this.payers.some((y) => y.id === x.id));
          this.payers = [...this.payers, ...uniquePayers];

          if (data.length === 0) {
            this.bottom = true;
            this.busy = false;
          }
          setTimeout(() => {
            this.busy = false;
          }, 500);

          return this.refreshOrderSubscriptionMatches().then(() => {
            this.fetchExistingOrdersSubscriptions();
          });
        })
        .catch(() => {
          this.error = true;
        });
    },
    clearAll() {
      this.$emit('update:selected', []);
    },
    toggleAll(v) {
      if (v) {
        this.request(true).then(() => {
          const toAdd = this.payers.filter((x) => !this.selected.some((y) => y.id === x.id));
          this.$emit('update:selected', this.selected.concat(toAdd));
          this.reloadOffer();
        });
      } else {
        const selected = this.selected.filter((x) => !this.payers.some((y) => y.id === x.id));
        this.$emit('update:selected', selected);
        this.reloadOffer();
      }
    },
    togglePayer(payer) {
      if (this.selected.some((x) => x.id === payer.id)) {
        this.$emit(
          'update:selected',
          this.selected.filter((x) => x.id !== payer.id),
        );
      } else {
        this.$emit('update:selected', this.selected.concat([payer]));
      }
      this.reloadOffer();
    },
    reloadOffer() {
      this.$nextTick(() => {
        if ((this.productIds.length === 0 && this.selectedListings.length === 0) || this.selected.length === 0) {
          this.offer = [];
          this.$emit('offer', []);
          return;
        }

        this.offerPending = true;
        this.findPayersOffer({
          params: {
            catalogId: this.catalogId,
          },
          data: {
            payerIds: this.selected.map((x) => x.id),
            productIds: this.productIds,
            listingIds: this.selectedListings,
            subscriptionsOnly: this.subscriptionsOnly,
          },
        })
          .then(({ data }) => {
            this.offer = data;
            this.$emit('offer', data);
            this.offerPending = false;
          })
          .catch(() => {
            this.offer = [];
            this.$emit('offer', []);
            this.offerPending = false;
          });
      });
    },
  },
  mounted() {
    this.pageExpand(true);
    this.updatePayersListHeight = () => {
      const payersList = this.$refs.payersList.getBoundingClientRect();
      this.payersListHeight = payersList.top;
    };
    this.updatePayersListHeight();
    window.addEventListener('resize', this.updatePayersListHeight);
  },
  destroyed() {
    this.pageExpand(false);
    window.removeEventListener('resize', this.updatePayersListHeight);
  },
  created() {
    this.request();
  },
};
</script>

<style lang="scss" scoped>
  @import '~@/components/payers/style.scss';

  .info-row {
    border-bottom: 1px solid #dddddd;
    font-size: 12px;
    font-weight: 400;
    padding-top: 3px;
    padding-bottom: 7px;
  }

  .sm-label {
    font-size: 12px;
  }

  .selected-clients {
    box-shadow: 1px 3px 8px #ddd;
    border-radius: 10px;
    padding: 10px;
  }
  .clear-all {
    cursor: pointer;
    font-size: 12px;

    &:hover {
      text-decoration: underline;
      color: $red !important;
    }
  }

  .selected-clients {
    .list-complete-item:hover {
      .close-btn {
        transition: color 250ms;
      }
    }

    .list-complete-item:hover {
      .close-btn {
        color: $red;
      }
    }
  }
</style>
